import { getRawInitData } from './telegram';

// Use the same public origin. Nginx proxies /api to the backend, avoiding the
// retired IP address and its invalid HTTPS certificate.
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');

class ApiService {
  async request(endpoint, options = {}) {
    const method = options.method || 'GET';
    const url = `${API_BASE_URL}${endpoint}`;

    const headers = {
      'Accept': 'application/json',
      ...options.headers,
    };

    const telegramInitData = getRawInitData();
    const telegramInitDataHeader = Object.keys(headers)
      .find((name) => name.toLowerCase() === 'x-telegram-init-data');
    if (telegramInitData && (!telegramInitDataHeader || !String(headers[telegramInitDataHeader] || '').trim())) {
      headers[telegramInitDataHeader || 'X-Telegram-Init-Data'] = telegramInitData;
    }

    if (method !== 'GET' && method !== 'HEAD') {
      headers['Content-Type'] = 'application/json';
    }

    const config = {
      method: method,
      mode: 'cors',
      credentials: 'omit',
      redirect: 'manual',
      headers: headers,
    };

    if (options.body) {
      if (typeof options.body === 'object') {
        config.body = JSON.stringify(options.body);
      } else {
        config.body = options.body;
      }
    }

    try {
      let response = await fetch(url, config);

      if (response.status === 307 || response.status === 308) {
        const redirectUrl = response.headers.get('Location');
        if (redirectUrl) {
          const redirectConfig = { ...config, redirect: 'follow' };
          response = await fetch(redirectUrl, redirectConfig);
        }
      }

      if (response.status === 0 || response.type === 'opaque') {
        throw new Error('CORS error: Unable to fetch resource');
      }

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch {
          data = { detail: text };
        }
      }

      if (!response.ok) {
        let msg = `HTTP error! status: ${response.status}`;
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            msg = data.detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
          } else if (typeof data.detail === 'string') {
            msg = data.detail;
          } else {
            msg = JSON.stringify(data.detail);
          }
        } else if (data.msg) {
          msg = data.msg;
        }
        const err = new Error(msg);
        err.status = response.status;
        throw err;
      }

      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  async ping() {
    const url = `${API_BASE_URL}/ping`;
    const res = await fetch(url, { method: 'GET', mode: 'cors' });
    if (!res.ok) throw new Error(`ping ${res.status}`);
    return res;
  }

  async createUser(userData) {
    return this.request('/users/', {
      method: 'POST',
      body: userData,
    });
  }

  async getUser(userId) {
    return this.request(`/users/${userId}`);
  }

  async getReferralLink(userId) {
    return this.request(`/users/${userId}/referral-link`);
  }

  async getReferralCount(userId) {
    return this.request(`/users/${userId}/referral-count`);
  }

  async getReferralRewards(userId) {
    return this.request(`/users/${userId}/referral-rewards`);
  }

  async getBonusBalance(userId) {
    return this.request(`/users/${userId}/bonus-balance`);
  }

  async getTransactions(userId) {
    return this.request(`/users/${userId}/transactions`);
  }

  async disableAutorenew(userId) {
    return this.request(`/users/${userId}/disable-autorenew`, { method: 'POST' });
  }

  async getDevices(userId) {
    return this.request(`/users/${Number(userId)}/devices`);
  }

  async revokeDevice(userId, deviceId) {
    return this.request(`/users/${Number(userId)}/devices/${Number(deviceId)}`, {
      method: 'DELETE',
    });
  }

  async restoreDevice(userId, deviceId) {
    return this.request(`/users/${Number(userId)}/devices/${Number(deviceId)}/restore`, {
      method: 'POST',
    });
  }

  async deviceSlotsCheckout(body) {
    const payload = {
      userid: Number(body.userid),
      slots: Number(body.slots),
      return_url: String(body.return_url || ''),
      payment_method: String(body.payment_method || 'card'),
    };
    if (body.email != null && String(body.email).trim()) {
      payload.email = String(body.email).trim();
    }
    return this.request('/devices/slots/checkout', { method: 'POST', body: payload });
  }

  async createKey(userId) {
    return this.request('/users/key/', {
      method: 'POST',
      body: { userid: Number(userId) },
    });
  }

  /** Шифрует URL подписки для Happ (happ://crypt4/...) — настройки серверов скрыты. */
  async getEncryptedHappLink(subscriptionUrl) {
    if (!subscriptionUrl || typeof subscriptionUrl !== 'string') return null;
    const url = encodeURIComponent(subscriptionUrl.trim());
    const res = await this.request(`/subscription/encrypt-for-happ?url=${url}`);
    return res?.happ_link || null;
  }

  async subscriptionCheckout(body) {
    const payload = {
      userid: Number(body.userid),
      tariff_plan: String(body.tariff_plan ?? 'pro'),
      duration_days: Number(body.duration_days),
      amount: String(body.amount),
      return_url: String(body.return_url ?? ''),
      autorenew_consent: body.autorenew_consent === true,
      autorenew_terms_version: String(body.autorenew_terms_version ?? '2026-08-12'),
    };
    if (body.payment_method != null && body.payment_method !== '') {
      payload.payment_method = String(body.payment_method);
    }
    if (body.email != null && String(body.email).trim() !== '') {
      payload.email = String(body.email).trim();
    }
    return this.request('/subscription/checkout', {
      method: 'POST',
      body: payload,
    });
  }

  async getPaymentStatus(paymentId) {
    return this.request(`/payments/status/${paymentId}`);
  }

  async updateKey(userId, key) {
    return this.request('/users/key', {
      method: 'PUT',
      body: {
        userid: Number(userId),
        user_key: String(key),
      },
    });
  }

  adminRequest(endpoint, options = {}) {
    return this.request(endpoint, options);
  }

  async adminMe() {
    return this.adminRequest('/admin/me');
  }

  async adminListAdmins() {
    return this.adminRequest('/admin/admins');
  }

  async adminAddAdmin(userid, username = null) {
    const body = { userid: Number(userid) };
    if (username) body.username = String(username).replace(/^@/, '').trim();
    return this.adminRequest('/admin/admins', { method: 'POST', body });
  }

  async adminRemoveAdmin(userid) {
    return this.adminRequest(`/admin/admins/${Number(userid)}`, { method: 'DELETE' });
  }

  async adminSearchUsers({ query = '', status = 'all', limit = 20 } = {}) {
    const p = new URLSearchParams();
    if (query) p.set('query', query);
    if (status) p.set('status', status);
    p.set('limit', String(limit));
    return this.adminRequest(`/admin/users/search?${p.toString()}`);
  }

  async adminUserCard(userid) {
    return this.adminRequest(`/admin/users/${Number(userid)}/card`);
  }

  async adminGrantDays(userid, days, tariffPlan = 'manual') {
    return this.adminRequest(`/admin/users/${Number(userid)}/grant-days`, {
      method: 'POST',
      body: { days: Number(days), tariff_plan: String(tariffPlan) },
    });
  }

  async adminGrantUntil(userid, endDate, tariffPlan = 'manual') {
    return this.adminRequest(`/admin/users/${Number(userid)}/grant-until`, {
      method: 'POST',
      body: { end_date: String(endDate), tariff_plan: String(tariffPlan) },
    });
  }

  async adminForceRefreshKey(userid) {
    return this.adminRequest(`/admin/users/${Number(userid)}/force-refresh-key`, { method: 'POST' });
  }

  async adminCancelSubscription(userid) {
    return this.adminRequest(`/admin/users/${Number(userid)}/cancel-subscription`, { method: 'POST' });
  }

  async adminFunnels(funnelId = 1, days = 30) {
    return this.adminRequest(`/admin/funnels?funnel_id=${Number(funnelId)}&days=${Number(days)}`);
  }

  async adminAdsList(limit = 60) {
    return this.adminRequest(`/admin/ads?limit=${Number(limit)}`);
  }

  async adminAdsCreate(code) {
    return this.adminRequest('/admin/ads', { method: 'POST', body: { code: String(code) } });
  }

  async adminGkReloadServers() {
    return this.adminRequest('/admin/gk/reload-servers', { method: 'POST' });
  }

  async adminGkReloadAndRefresh() {
    return this.adminRequest('/admin/gk/reload-and-refresh', { method: 'POST' });
  }

  async adminStatsTimeseries({ period, days, date_from, date_to } = {}) {
    const p = new URLSearchParams();
    if (period) p.set('period', period);
    if (days) p.set('days', String(days));
    if (date_from) p.set('date_from', date_from);
    if (date_to) p.set('date_to', date_to);
    const qs = p.toString();
    return this.adminRequest(`/admin/stats/timeseries${qs ? `?${qs}` : ''}`);
  }

  async adminUsersInRange({ period, days, date_from, date_to, status, sort, order, limit, offset } = {}) {
    const p = new URLSearchParams();
    if (period) p.set('period', period);
    if (days) p.set('days', String(days));
    if (date_from) p.set('date_from', date_from);
    if (date_to) p.set('date_to', date_to);
    if (status) p.set('status', status);
    if (sort) p.set('sort', sort);
    if (order) p.set('order', order);
    p.set('limit', String(limit ?? 50));
    p.set('offset', String(offset ?? 0));
    return this.adminRequest(`/admin/stats/users-in-range?${p.toString()}`);
  }

  async adminExportUserDay({ period, days, date_from, date_to } = {}) {
    const p = new URLSearchParams();
    if (period) p.set('period', period);
    if (days) p.set('days', String(days));
    if (date_from) p.set('date_from', date_from);
    if (date_to) p.set('date_to', date_to);
    const telegramInitData = getRawInitData();
    const headers = telegramInitData ? { 'X-Telegram-Init-Data': telegramInitData } : {};
    const res = await fetch(`${API_BASE_URL}/admin/stats/export-user-day?${p.toString()}`, { headers });
    if (!res.ok) throw new Error(`Экспорт: HTTP ${res.status}`);
    return res.blob();
  }

  /**
   * Fire-and-forget funnel event tracking.
   * Never throws — safe to call without await.
   */
  trackEvent({ funnel_id, step, user_id = null, username = '', payload = {} }) {
    this.request('/track-event', {
      method: 'POST',
      body: { funnel_id, step, user_id: user_id ?? null, username: username ?? '', payload: payload ?? {} },
    }).catch(() => {});
  }
}

const apiService = new ApiService();
export default apiService;
export { API_BASE_URL };

/**
 * Возвращает постоянный URL подписки если есть subscription_token, иначе fallback на user_key.
 * Принимает объект пользователя или ответ от POST /users/key/.
 */
export function getStableSubscriptionUrl(userOrResponse) {
  const token = (userOrResponse?.subscription_token || '').trim();
  if (token) {
    const apiBase = /^https?:\/\//i.test(API_BASE_URL)
      ? API_BASE_URL
      : `${window.location.origin}${API_BASE_URL.startsWith('/') ? '' : '/'}${API_BASE_URL}`;
    return `${apiBase}/subscription/${token}.txt`;
  }

  const key = (userOrResponse?.user_key || '').trim();
  return key || null;
}
